import React, { useState } from "react";
import { useRouter } from "next/router";
import styled, { css } from "styled-components";
import { ImageProps, layout, media } from "@literati/public-ui-shared-react";
import { SubscriptionShippingWindow } from "@literati/kids-api";
import { CheckoutSession, Invoice } from "lib/api/checkout";
import { Subscription, SubscriptionPlanCamelCase } from "lib/api/subscription";
import LoadingAnimation from "@/atoms/LoadingAnimation";
import StepLayout, {
  StepLayoutClassName,
  StepLayoutProps,
} from "components/Step/StepLayout";
import {
  NormalizedOrderSummary,
  PaymentValues,
} from "components/Step/stepTypes";
import { CheckoutSectionCard } from "components/Step/StepPrimitives";
import PaymentSectionExpectedBy from "components/Payment/PaymentSectionExpectedBy";
import PaymentFormPaymentMethod, {
  PaymentFormId,
} from "components/Payment/PaymentFormPaymentMethod";
import PaymentSectionOrderDetails from "components/Payment/PaymentSectionOrderDetails";
import { postCompleteGuidedQuiz } from "lib/api/onboarding";
import { eventSubscription } from "analytics/ga4";
import { PlanPrepaidPricing } from "components/PlanSelection/planSelectionUtil";
import useWindowStorage from "utils/hooks/useWindowStorage";
import { PENDING_COUPON_STORAGE_KEY } from "utils/hooks/useExcludeFromAnnual";
import { MembershipOfferBanner } from "components/Membership/MembershipOfferBanner";
import { FEATURE_FLAG_SHOW_FALL25_COUPON } from "components/Membership/membershipFeatureFlags";
import {
  FALL25_COUPON_CODE,
  getOfferHeadingCssOverride,
  isCouponClaimedByQuery,
} from "components/Step/stepUtil";

const Content = styled.div`
  display: flex;
  justify-content: center;
  margin: 1rem 0 0;
  padding-bottom: 2rem;
  width: 100vw;

  ${media.lg} {
    margin: 1.5rem 0 0;
  }
`;

const flexColumnCenter = css`
  display: flex;
  flex-direction: column;
  align-items: stretch;
`;

const StyledDiv = styled.div`
  ${layout.contentSpacing}
  ${flexColumnCenter}
  width: 100%;
  max-width: 38rem;

  ${media.lg} {
    flex-direction: row;
    align-items: flex-start;
    max-width: 72rem;
    gap: 1rem;
  }
`;

const Column = styled.div`
  ${flexColumnCenter}
  flex-basis: 50%;
  width: 100%;
`;

const StyledCheckoutSectionCard = styled(CheckoutSectionCard)`
  margin: 1rem 0 0;
  width: 100%;
`;

const StyledCheckoutSectionCardExpectedBy = styled(StyledCheckoutSectionCard)`
  margin: 0;
  justify-content: space-between;
`;

const StyledCheckoutSectionCardPayment = styled(StyledCheckoutSectionCard)`
  flex-direction: column;
  align-items: stretch;
  padding: 2rem 1.25rem;
`;

const StyledCheckoutSectionCardOrderSummary = styled(StyledCheckoutSectionCard)`
  flex-direction: column;
  align-items: stretch;
  padding: 1.5rem;

  ${media.lg} {
    flex-basis: 50%;
    margin: 0;
  }
`;

const StyledHeading = styled.div`
  margin-top: 2rem;
`;

export type FormikSubmitStepPayment = (values: PaymentValues) => void;

export type StepLayoutPropsPayment = Omit<
  StepLayoutProps<object>,
  "asForm" | "children" | "formId"
>;

export interface StepPaymentProps extends StepLayoutPropsPayment {
  checkoutSession?: CheckoutSession | null;
  contentfulQuizModelParsed?: any;
  contentfulQuizModelRaw?: any;
  onSubmit?: FormikSubmitStepPayment;
  orderSummary?: NormalizedOrderSummary | null;
  phoneNumber?: string | null;
  selectedPlan?: SubscriptionPlanCamelCase | null;
  selectPlanImageProps?: ImageProps | null;
  selectedPlanPrepaidPricing?: PlanPrepaidPricing | null;
  shippingWindow?: SubscriptionShippingWindow | null;
}

export const StepPayment = ({
  checkoutSession,
  contentfulQuizModelParsed,
  contentfulQuizModelRaw,
  onSubmit,
  orderSummary,
  selectedPlanPrepaidPricing,
  phoneNumber,
  selectedPlan,
  selectPlanImageProps,
  shippingWindow,
  stepFooterProps,
  ...stepLayoutProps
}: StepPaymentProps) => {
  const router = useRouter();
  const [_, { setItem }] = useWindowStorage("localStorage");
  const [disabled, setDisabled] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [purchaseSucceeded, setPurchaseSucceeded] = useState(false);
  const [form, setForm] = useState<PaymentFormId | undefined>();
  const isFall25CouponClaimed =
    FEATURE_FLAG_SHOW_FALL25_COUPON &&
    isCouponClaimedByQuery(router.query, FALL25_COUPON_CODE);

  const heading = isFall25CouponClaimed ? (
    <div>
      <MembershipOfferBanner isClaimed />
      <StyledHeading>Payment</StyledHeading>
    </div>
  ) : (
    "Payment"
  );

  const cssOverride = getOfferHeadingCssOverride(
    isFall25CouponClaimed,
    StepLayoutClassName.HEADING
  );

  const handleFormIdChange = (formId: PaymentFormId) => {
    setForm(formId);
  };

  const handleDisableSubmitChange = (disable: boolean) => {
    setDisabled(disable);
  };

  const handleSubmitting = (submitting: boolean) => {
    setSubmitting(submitting);
  };

  const handlePurchaseSuccess = async (
    invoice: Invoice,
    subscription: Subscription | null
  ) => {
    setPurchaseSucceeded(true);

    // Reporting on purchase pixels
    if (subscription && invoice) {
      const res = await postCompleteGuidedQuiz({
        quizSlug: contentfulQuizModelParsed?.slug,
        quizVersion: contentfulQuizModelParsed?.version,
        subscriptionId: String(subscription.id),
      });
      const estimatedLTV = String(res?.ltvEstimate);

      window.dataLayer?.push({
        event: "purchase_confirmation",
        eventCategory: "Quiz",
        // LiveConnect Tag - product purchase success page tag data
        kidsAccountEmail: checkoutSession?.accountInfo?.email || "",
        kidsInvoiceOrderValue: invoice?.orderValue || "",
        kidsInvoiceCurrencyType: "USD",
        kidsInvoiceId: invoice?.id?.toString() || "",
        kidsInvoicePlanSlug: invoice?.items?.[0]?.object?.planSlug || "",
        // GA, Microsoft UET
        estimatedLTV,
      });

      eventSubscription({
        estimatedLTV,
        value: String(invoice?.orderValue),
      });

      setItem(PENDING_COUPON_STORAGE_KEY, null);
    }

    const values: PaymentValues = {
      kind: "payment",
      invoice,
      subscription,
      phoneNumber: phoneNumber || undefined,
    };
    onSubmit?.(values);
  };

  return (
    <StepLayout
      // multiple self-contained forms on page, so we don't use the asForm args
      asForm={false}
      bgColor="papierBlanc"
      css={cssOverride}
      disableContentSpacing
      heading={heading}
      stepFooterProps={{
        nextButtonProps: {
          disabled: disabled || purchaseSucceeded,
          form,
          loading: submitting,
          type: "submit",
        },
        ...stepFooterProps,
      }}
      suppressFooter={form === "paypal-form"}
      {...stepLayoutProps}
    >
      <Content css="width: 100vw">
        <StyledDiv>
          <Column>
            {shippingWindow ? (
              <StyledCheckoutSectionCardExpectedBy>
                <PaymentSectionExpectedBy shippingWindow={shippingWindow} />
              </StyledCheckoutSectionCardExpectedBy>
            ) : null}
            <StyledCheckoutSectionCardPayment>
              {checkoutSession && orderSummary && selectedPlan ? (
                <PaymentFormPaymentMethod
                  checkoutSession={checkoutSession}
                  contentfulQuizModelRaw={contentfulQuizModelRaw}
                  onDisableSubmit={handleDisableSubmitChange}
                  onFormIdChange={handleFormIdChange}
                  onSubmitting={handleSubmitting}
                  onPurchaseSuccess={handlePurchaseSuccess}
                  orderSummary={orderSummary}
                  plan={selectedPlan}
                  redeemingGift={false}
                  selectedPlanPrepaidPricing={selectedPlanPrepaidPricing}
                  phoneNumber={phoneNumber}
                />
              ) : null}
            </StyledCheckoutSectionCardPayment>
          </Column>
          <StyledCheckoutSectionCardOrderSummary>
            <PaymentSectionOrderDetails
              boxImageProps={selectPlanImageProps}
              giftCode={checkoutSession?.giftCode}
              orderSummary={orderSummary}
              plan={selectedPlan}
            />
          </StyledCheckoutSectionCardOrderSummary>
        </StyledDiv>
      </Content>
      {submitting ? <LoadingAnimation message="Submitting Payment..." /> : null}
    </StepLayout>
  );
};

export default StepPayment;
